import { useState } from 'react';
import { CreateWorkflowNode, LLMCommonConfiguration, Prompt } from 'api/model';
import FormBlock from 'components/form/FormBlock';
import { useParams } from 'react-router';
import usePrompts from 'api/prompts/usePrompts';
import { NodeFormProps } from 'api/workflows-steps/formSchema';
import { useNodeValidation } from 'hooks/useNodeValidation';

export const DEFAULT_NEW_PROMPT_REVISION = 1;

export type LLMCommonFormProps = NodeFormProps & {
  validate?: boolean;
  onConfigurationChange?: (
    newConfiguration: Record<string, any>,
    previousConfiguration?: Record<string, any>,
    prompts?: Prompt[],
  ) => void;
};

const LLMCommonForm = (props: LLMCommonFormProps) => {
  const { validate = true, onConfigurationChange } = props;
  const { projectId } = useParams();
  const { prompts } = usePrompts(projectId, true);
  const [formData, setFormData] = useState<CreateWorkflowNode>(props.initialValues || ({} as CreateWorkflowNode));

  const { validate: validateNode } = useNodeValidation(formData.type);

  const handleOnChange = (newFormData: CreateWorkflowNode) => {
    const newConfiguration = newFormData.configuration as LLMCommonConfiguration | undefined;
    if (!newConfiguration) {
      setFormData(newFormData);
      props.onChange?.(newFormData);
      return;
    }

    const configuration = formData.configuration as LLMCommonConfiguration | undefined;

    const promptId = newConfiguration.prompt_id;
    if (!promptId) {
      newConfiguration.prompt_revision = undefined;
    } else if (promptId !== configuration?.prompt_id) {
      const prompt = prompts?.find((p) => p.id === promptId);
      newConfiguration.prompt_revision = prompt ? prompt.revision : DEFAULT_NEW_PROMPT_REVISION;
    }

    onConfigurationChange?.(newConfiguration, configuration ?? {}, prompts);

    const updatedFormData = { ...newFormData, configuration: newConfiguration };
    setFormData(updatedFormData);
    props.onChange?.(updatedFormData);
  };

  const handleSubmit = async (newFormData: CreateWorkflowNode) => {
    if (validate && !(await validateNode(newFormData))) {
      return;
    }

    setFormData(newFormData);
    props.onSubmit?.(newFormData);
  };

  return <FormBlock {...props} onSubmit={handleSubmit} onChange={handleOnChange} />;
};

export default LLMCommonForm;
